import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Navigate, NavLink } from 'react-router-dom';
import { AuthContext } from '../auth/AuthContext';
import api from '../services/api';
import { respondToTicket, updateTicketStatus } from '../services/support';
import './Workboards.css';
import './SupportIssuesBoard.css';

const columns = [
  ['open', 'Open'],
  ['in_progress', 'In progress'],
  ['resolved', 'Resolved'],
  ['closed', 'Closed'],
];
const cardsPerPage = 10;

const dateLabel = (value) => value ? new Intl.DateTimeFormat('en-ZA', {
  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
}).format(new Date(value)) : '';

async function fetchTickets(q) {
  const first = await api.get('/support', { params: { q: q || undefined, page: 1, limit: 200 } });
  const pages = Math.max(1, Number(first.data.pages) || 1);
  const items = [...(first.data.items || [])];
  for (let page = 2; page <= pages; page += 1) {
    const response = await api.get('/support', { params: { q: q || undefined, page, limit: 200 } });
    items.push(...(response.data.items || []));
  }
  return items;
}

export default function SupportIssuesBoard() {
  const { role, user } = useContext(AuthContext) || {};
  const [tickets, setTickets] = useState([]);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [columnPages, setColumnPages] = useState({});

  const load = useCallback(async () => {
    setError('');
    try {
      setTickets(await fetchTickets(query));
    } catch (err) {
      setError(err?.response?.data?.error || 'Unable to load support issues.');
    }
  }, [query]);

  useEffect(() => { if (['admin', 'superadmin', 'development_board'].includes(role)) load(); }, [load, role]);

  const grouped = useMemo(() => Object.fromEntries(columns.map(([status]) => [
    status, tickets.filter((ticket) => ticket.status === status),
  ])), [tickets]);

  const setColumnPage = (status, page) => {
    setColumnPages((current) => ({ ...current, [status]: page }));
  };

  if (!['admin', 'superadmin', 'development_board'].includes(role)) return <Navigate to="/boards/login" replace />;

  const updateLocal = (ticket) => {
    setTickets((current) => current.map((item) => item._id === ticket._id ? ticket : item));
    setSelected((current) => current?._id === ticket._id ? ticket : current);
  };

  const openTicket = async (id) => {
    setError('');
    try {
      const { data } = await api.get(`/support/${id}`);
      setSelected(data);
      setReply('');
    } catch (err) {
      setError(err?.response?.data?.error || 'Unable to open support issue.');
    }
  };

  const move = async (id, status) => {
    if (!columns.some(([key]) => key === status)) return;
    setBusy(true);
    setError('');
    try {
      updateLocal(await updateTicketStatus(id, status));
    } catch (err) {
      setError(err?.response?.data?.error || 'Unable to update support issue.');
    } finally {
      setBusy(false);
    }
  };

  const sendReply = async (event) => {
    event.preventDefault();
    if (!selected || !reply.trim()) return;
    setBusy(true);
    setError('');
    try {
      updateLocal(await respondToTicket(selected._id, reply.trim()));
      setReply('');
    } catch (err) {
      setError(err?.response?.data?.error || 'Unable to send response.');
    } finally {
      setBusy(false);
    }
  };

  const details = selected?.structured || {};

  return <main className="workboard-shell support-board">
    <header className="workboard-header">
      <div><p className="board-eyebrow">APEX SUPPORT</p><h1>Support Issues board</h1><p>Support-module messages · signed in as {user?.email}</p></div>
      <nav className="workboard-actions" aria-label="Boards">
        <NavLink to="/boards/bugs">Bugs</NavLink>
        <NavLink to="/boards/features">Features</NavLink>
        <NavLink to="/boards/support">Support Issues</NavLink>
        {['admin', 'superadmin'].includes(role) && <NavLink to="/admin/support">Ticket list</NavLink>}
      </nav>
    </header>
    <form className="workboard-toolbar" onSubmit={(event) => { event.preventDefault(); setColumnPages({}); setQuery(search.trim()); }}>
      <input aria-label="Search support issues" placeholder="Search support messages" value={search} onChange={(event) => setSearch(event.target.value)} />
      <button type="submit">Search</button>
      <button type="button" onClick={load}>Refresh</button>
    </form>
    {error && <p className="board-error" role="alert">{error}</p>}
    <section className="support-kanban" aria-label="Support Issues kanban board">
      {columns.map(([status, label]) => {
        const pageCount = Math.max(1, Math.ceil(grouped[status].length / cardsPerPage));
        const page = Math.min(columnPages[status] || 1, pageCount);
        const visibleTickets = grouped[status].slice((page - 1) * cardsPerPage, page * cardsPerPage);
        return <article className="kanban-column" key={status} onDragOver={(event) => event.preventDefault()} onDrop={(event) => { const id = event.dataTransfer.getData('text/support-ticket'); if (id && !busy) move(id, status); }}>
        <header><h2>{label}</h2><span>{grouped[status].length}</span></header>
        <div className="kanban-cards">
          {visibleTickets.map((ticket) => <section className="work-card support-card" key={ticket._id} draggable onDragStart={(event) => event.dataTransfer.setData('text/support-ticket', ticket._id)}>
            <div className="card-top"><span className={`criticality ${ticket.structured?.impactLevel === 'blocker' ? 'critical' : ticket.structured?.impactLevel || 'medium'}`}>{ticket.structured?.impactLevel || 'medium'}</span><span>{ticket.role}</span></div>
            <button type="button" className="card-title" onClick={() => openTicket(ticket._id)}>{ticket.subject}</button>
            <p>{ticket.message}</p>
            <footer><span>{ticket.name || ticket.email || 'User'} · {dateLabel(ticket.createdAt)}</span></footer>
            <select aria-label={`Status for ${ticket.subject}`} value={ticket.status} disabled={busy} onChange={(event) => move(ticket._id, event.target.value)}>{columns.map(([key, name]) => <option key={key} value={key}>{name}</option>)}</select>
          </section>)}
        </div>
        {pageCount > 1 && <nav className="support-column-pagination" aria-label={`${label} cards pages`}>
          <button type="button" disabled={page === 1} onClick={() => setColumnPage(status, page - 1)}>Previous</button>
          <span>Page {page} of {pageCount}</span>
          <button type="button" disabled={page === pageCount} onClick={() => setColumnPage(status, page + 1)}>Next</button>
        </nav>}
      </article>;
      })}
    </section>
    {selected && <div className="board-modal-backdrop" onClick={() => setSelected(null)}>
      <section className="board-modal support-detail" role="dialog" aria-modal="true" aria-label={`Support issue: ${selected.subject}`} onClick={(event) => event.stopPropagation()}>
        <header><h2>{selected.subject}</h2><button type="button" className="icon-button" aria-label="Close support issue" onClick={() => setSelected(null)}>x</button></header>
        <p><strong>From:</strong> {selected.name || 'User'} ({selected.email}) · {selected.role}</p>
        <p><strong>Submitted:</strong> {dateLabel(selected.createdAt)} · <strong>Status:</strong> {selected.status}</p>
        <p><strong>Type:</strong> {details.issueType || selected.category || 'General'} · <strong>Impact:</strong> {details.impactLevel || 'medium'}</p>
        <div className="support-message">{selected.message}</div>
        {details.stepsToReproduce && <p><strong>Steps to reproduce:</strong> {details.stepsToReproduce}</p>}
        {details.expectedBehavior && <p><strong>Expected:</strong> {details.expectedBehavior}</p>}
        {details.actualBehavior && <p><strong>Actual:</strong> {details.actualBehavior}</p>}
        <h3>Responses</h3>
        {(selected.responses || []).length ? selected.responses.map((response, index) => <div className="support-response" key={`${response.at}-${index}`}><small>{dateLabel(response.at)}</small><p>{response.message}</p></div>) : <p>No responses yet.</p>}
        <form onSubmit={sendReply}><label>Reply<textarea value={reply} onChange={(event) => setReply(event.target.value)} required /></label><button disabled={busy || !reply.trim()}>{busy ? 'Sending...' : 'Send response'}</button></form>
      </section>
    </div>}
  </main>;
}
